import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";

export default function AgentChatInput({ onSend, isSending, placeholder }) {
    const { t } = useTranslation();
    const [message, setMessage] = useState("");
    const [focused, setFocused] = useState(false);
    const textareaRef = useRef(null);

    const canSend = message.trim() && !isSending;

    const resizeTextarea = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = Math.min(el.scrollHeight, 120) + "px";
    }, []);

    useEffect(() => { resizeTextarea(); }, [message, resizeTextarea]);
    useEffect(() => { textareaRef.current?.focus(); }, []);

    const handleSubmit = () => {
        if (!canSend) return;
        onSend(message.trim());
        setMessage("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div className="px-3 py-3 border-t bg-white">
            <div className={`bg-gray-50 border rounded-xl transition-all duration-200 ${
                focused ? "border-purple-300 ring-2 ring-purple-100 shadow-sm" : "border-gray-200"
            }`}>
                <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    placeholder={placeholder || t("builder.chat.placeholder")}
                    disabled={isSending}
                    rows={1}
                    className="w-full bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none disabled:opacity-50 px-3.5 pt-3 pb-1 resize-none leading-relaxed"
                />
                <div className="flex items-center justify-between px-3 pb-2">
                    <p className="text-[10px] text-gray-400">
                        <kbd className="px-1 py-0.5 bg-gray-100 border rounded text-[9px] font-mono">Enter</kbd> {t("builder.chat.send")}
                        <span className="mx-1">·</span>
                        <kbd className="px-1 py-0.5 bg-gray-100 border rounded text-[9px] font-mono">Shift+Enter</kbd> {t("builder.chat.newLine")}
                    </p>
                    {isSending ? (
                        <div className="w-7 h-7 flex items-center justify-center">
                            <div className="w-4 h-4 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!canSend}
                            className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200 ${
                                canSend
                                    ? "bg-purple-600 hover:bg-purple-700 text-white shadow-sm"
                                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                        >
                            <Icon name="Send" className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
